import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setToken, getToken, setRefresh, ensureAuthCookie, IMP_TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking, false = logged out, obj = logged in

  const applySession = (data) => {
    if (data?.access_token) setToken(data.access_token);
    if (data?.refresh_token) setRefresh(data.refresh_token);
    setUser(data);
    ensureAuthCookie();
  };

  const fetchMe = useCallback(async () => {
    if (!getToken()) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      ensureAuthCookie();
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(IMP_TOKEN_KEY);
    localStorage.removeItem("ff_demo");
    localStorage.removeItem("ff_demo_welcome_seen");
    setToken(null);
    setRefresh(null);
    setUser(false);
  }, []);

  // ---- idle auto-logout removed: users stay signed in until they explicitly Sign Out ----

  const login = async (email, password, remember) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    applySession(data);
    return data;
  };

  const loginDemo = async () => {
    const { data } = await api.post("/demo/login");
    localStorage.setItem("ff_demo", "1");
    applySession(data);
    return data;
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
    await fetchMe();
  };

  const impersonate = async (uid) => {
    // keep current (super-admin) token to restore later
    localStorage.setItem(IMP_TOKEN_KEY, getToken() || "");
    const { data } = await api.post(`/users/${uid}/login-as`);
    applySession(data);
    return data;
  };

  const exitImpersonation = async () => {
    const orig = localStorage.getItem(IMP_TOKEN_KEY);
    localStorage.removeItem(IMP_TOKEN_KEY);
    if (orig) { setToken(orig); await fetchMe(); }
    else await logout();
  };

  const isSuperAdmin = user && user.access_level === "super_admin";
  const has = useCallback((key) => {
    if (!user) return false;
    if (user.access_level === "super_admin") return true;
    return (user.effective_permissions || []).includes(key);
  }, [user]);
  const canModule = useCallback((moduleKey) => has(`${moduleKey}.view`), [has]);

  const isDemo = !!(user && (user.workspace === "demo")) || localStorage.getItem("ff_demo") === "1";
  const value = {
    user, setUser, login, loginDemo, logout, fetchMe, changePassword,
    impersonate, exitImpersonation, applySession,
    isSuperAdmin, has, canModule, isDemo,
    isImpersonating: !!(user && user.impersonating),
    plants: (user && user.plants) || [],
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
